import { compact } from 'lodash';

import { AIOnboardingModalOrganizationField } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { AiOnboardingModal_HouseholdFragment } from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { getKgV2NodeIDFromSuggestion } from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { AugmentedCreateClientOrganizationInput } from '@/types/schema';
import { sentenceCaseWithAcronyms } from '@/utils/textFormatting';

///////////////////////////////////////////////////////////
// SUGGESTION DATA -> FORM DATA
///////////////////////////////////////////////////////////

/**
 * Maps the ONBOARDING_ORGANIZATION suggestion to form data.
 *
 * @param suggestions
 */
export function mapOrganizationsToFormData(
  suggestions: AiSuggestionForReviewCard[]
): AIOnboardingModalOrganizationField[] {
  return compact(
    suggestions.map((s) => {
      if (!s.onboardingOrganization) return null;

      return {
        _suggestionID: s.id,
        organizationName:
          s.onboardingOrganization?.displayName ??
          sentenceCaseWithAcronyms(
            s.onboardingOrganization?.organizationName || ''
          ),
        organizationKind: s.onboardingOrganization?.organizationKind || '',
      };
    })
  );
}

export function getEmptyOrganizationField(): AIOnboardingModalOrganizationField {
  return {
    _suggestionID: '',
    organizationName: '',
    organizationKind: '',
  };
}

///////////////////////////////////////////////////////////
// FORM DATA -> MUTATION INPUT
///////////////////////////////////////////////////////////

/**
 * Maps the form data to the mutation input.
 *
 * @param organizations
 * @param household
 * @param suggestionsByID
 */
export function mapOrganizationsToInput(
  organizations: AIOnboardingModalOrganizationField[],
  household: AiOnboardingModal_HouseholdFragment,
  suggestionsByID: Record<string, AiSuggestionForReviewCard>
): AugmentedCreateClientOrganizationInput[] {
  return organizations.map((o) => {
    const suggestion = o._suggestionID
      ? suggestionsByID[o._suggestionID]
      : undefined;
    const kgV2NodeID = getKgV2NodeIDFromSuggestion(suggestion);

    return {
      create: {
        householdID: household.id,
        name: o.organizationName,
        kind: o.organizationKind || undefined,
        onboardedOrganizationSuggestionIDs: o._suggestionID
          ? [o._suggestionID]
          : undefined,
        kgV2NodeIDs: kgV2NodeID ? [kgV2NodeID] : undefined,
      },
    };
  });
}
